/**
 * Collection of menus and discounts available in a specific location.
 * Use a [draft catalog](https://dev.wix.com/api/rest/wix-restaurants/catalogs/draft-catalogs) to publish multiple changes to a catalog at the same time.
 * You can read more about catalogs in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Catalog {
  /**
   * Catalog ID.
   * @readonly
   */
  id?: string | null;
  /**
   * ID of the location the catalog belongs to. See the [Locations API](https://dev.wix.com/api/rest/business-info/locations/introduction) for more details.
   * @readonly
   * @format GUID
   */
  locationId?: string | null;
  /**
   * Whether the catalog is archived.
   * __Note:__ Archived catalogs can't be updated.
   * @readonly
   */
  archived?: boolean;
  /**
   * Date and time the last time a draft catalog has been published in `yyyy-mm-ddThh:mm:sssZ` format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   * @readonly
   */
  draftPublishedDate?: Date | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CatalogUpdated {
  /** @format GUID */
  metaSiteId?: string;
  /** @format GUID */
  locationId?: string | null;
  /** @format GUID */
  catalogId?: string | null;
  archived?: boolean;
  catalogItems?: Item[];
  catalogSections?: Section[];
  catalogMenus?: Menu[];
  catalogDiscounts?: Discount[];
}

/**
 * Anything that customers can buy in the restaurant. Items can be of type `DISH` or `VARIATION`.
 * You can read more about items in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Item {
  /**
   * Item ID.
   * @readonly
   */
  itemId?: string | null;
  /** Item name. */
  name?: string | null;
  /** Item description. */
  description?: string | null;
  /** Item price. */
  price?: Money;
  /** URL of the item's image file. */
  imageUrl?: string | null;
  /** Item labels. For example spicy, hot, vegan, gluten-free, or organic. */
  labels?: Labels;
  /** Whether the item is in stock. */
  inStock?: boolean | null;
  /**
   * Tax rate of the item in percent.
   * @decimalValue options { maxScale:3 }
   */
  taxRate?: string | null;
  /**
   * Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated.
   * @readonly
   */
  archived?: boolean | null;
  /**
   * Visibility criteria that must be met for the item to appear in the live site.
   * In case of multiple visibility criteria, every criterion must be fulfilled.
   */
  visibilityCriteria?: VisibilityCriteria;
  /** Items customers can choose to modify a dish. Can be an extra, selection, or deselection. */
  dishOptions?: DishOptions;
  /** Whether a customer can add a special request when ordering this item. Defaults to `true`. */
  acceptSpecialRequest?: boolean | null;
  /**
   * Item type.
   * @readonly
   */
  type?: ItemTypeWithLiterals;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @format CURRENCY
   * @readonly
   */
  currency?: string;
}

export interface Labels {
  values?: string[];
}

export interface VisibilityCriteria {
  /**
   * Whether the entity appears in the live site. Defaults to `true`.
   * If `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled.
   */
  visible?: boolean | null;
  /**
   * Fulfillment types.
   * @minSize 1
   * @maxSize 2
   */
  fulfillmentTypes?: FulfillmentTypeWithLiterals[];
  /**
   * Ordering platforms.
   * @minSize 1
   * @maxSize 5
   */
  platforms?: PlatformWithLiterals[];
  /** Time periods when the entity is available. */
  availability?: Availability;
}

export enum FulfillmentType {
  UNSPECIFIED_FULFILLMENT_TYPE = 'UNSPECIFIED_FULFILLMENT_TYPE',
  DELIVERY = 'DELIVERY',
  PICKUP_OR_DINE_IN = 'PICKUP_OR_DINE_IN',
}

/** @enumType */
export type FulfillmentTypeWithLiterals =
  | FulfillmentType
  | 'UNSPECIFIED_FULFILLMENT_TYPE'
  | 'DELIVERY'
  | 'PICKUP_OR_DINE_IN';

export enum Platform {
  UNSPECIFIED_PLATFORM = 'UNSPECIFIED_PLATFORM',
  SITE = 'SITE',
  MOBILE_SITE = 'MOBILE_SITE',
  WIX_APP = 'WIX_APP',
  CALL_CENTER = 'CALL_CENTER',
  CHAT_BOT = 'CHAT_BOT',
}

/** @enumType */
export type PlatformWithLiterals =
  | Platform
  | 'UNSPECIFIED_PLATFORM'
  | 'SITE'
  | 'MOBILE_SITE'
  | 'WIX_APP'
  | 'CALL_CENTER'
  | 'CHAT_BOT';

export interface Availability {
  /**
   * Weekly recurring time periods when the entity is available.
   * Limited to 100 time periods.
   */
  periods?: TimePeriod[];
  /** Exceptions to the entity's regular availability. The entity can be available or not available during the special hour period. */
  specialHourPeriods?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the entity is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

export enum DayOfWeek {
  UNDEFINED = 'UNDEFINED',
  SUN = 'SUN',
  MON = 'MON',
  TUE = 'TUE',
  WED = 'WED',
  THU = 'THU',
  FRI = 'FRI',
  SAT = 'SAT',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'UNDEFINED'
  | 'SUN'
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /** Whether the item is available during the exception. Defaults to `true`. */
  available?: boolean;
  /** Name of the special hour period. In the dashboard, the special hour period is called event. */
  eventName?: string | null;
}

export interface DishOptions {
  values?: DishOption[];
}

export interface DishOption extends DishOptionMethodOneOf {
  /** Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish. */
  selection?: Selection;
  /** Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish. */
  extras?: Extras;
  /** Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish. */
  deselection?: Deselection;
  /** Dish option name. */
  name?: string;
  /** @readonly */
  type?: TypeWithLiterals;
}

/** @oneof */
export interface DishOptionMethodOneOf {
  /** Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish. */
  selection?: Selection;
  /** Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish. */
  extras?: Extras;
  /** Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish. */
  deselection?: Deselection;
}

export interface DishOptionItem {
  /** Item ID of the dish option. */
  itemId?: string;
  /** Dish option price. `0` for free choices that are included in the dish's price. */
  price?: Money;
}

export interface Selection {
  /** Item ID of the default choice. */
  defaultChoice?: string | null;
  /** Item IDs of the available choices. */
  availableChoices?: DishOptionItem[];
}

export interface Extras {
  /** Item IDs of the default choices. */
  defaultChoices?: string[];
  /**
   * Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.
   * @max 2147483647
   */
  minChoices?: number | null;
  /**
   * Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.
   * @max 2147483647
   */
  maxChoices?: number | null;
  /** Item IDs of the available choices. */
  availableChoices?: DishOptionItem[];
}

export interface Deselection {
  /** Item IDs of the available choices. */
  availableChoices?: DishOptionItem[];
}

export enum Type {
  UNSPECIFIED_DISH_OPTION_TYPE = 'UNSPECIFIED_DISH_OPTION_TYPE',
  SELECTION = 'SELECTION',
  EXTRAS = 'EXTRAS',
  DESELECTION = 'DESELECTION',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNSPECIFIED_DISH_OPTION_TYPE'
  | 'SELECTION'
  | 'EXTRAS'
  | 'DESELECTION';

export enum ItemType {
  UNSPECIFIED_ITEM_TYPE = 'UNSPECIFIED_ITEM_TYPE',
  DISH = 'DISH',
  VARIATION = 'VARIATION',
}

/** @enumType */
export type ItemTypeWithLiterals =
  | ItemType
  | 'UNSPECIFIED_ITEM_TYPE'
  | 'DISH'
  | 'VARIATION';

/**
 * Collection of dishes that customers can buy in the restaurant.
 * You can read more about sections in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Section {
  /**
   * Section ID.
   * @readonly
   */
  sectionId?: string | null;
  /**
   * ID of the menu the section belongs to.
   * @readonly
   */
  menuId?: string | null;
  /** Section name. */
  name?: string | null;
  /** Section description. */
  description?: string | null;
  /** URL of the section's image file. */
  imageUrl?: string | null;
  /** IDs of the items that belong to the section. */
  itemIds?: SectionItemIds;
  /**
   * Visibility criteria that must be met for the section to appear in the live site.
   * In case of multiple visibility criteria, every criterion must be fulfilled.
   */
  visibilityCriteria?: VisibilityCriteria;
  /**
   * Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated.
   * @readonly
   */
  archived?: boolean | null;
}

/** Item IDs of the dishes that belong to this section. */
export interface SectionItemIds {
  values?: string[];
}

/**
 * Collection of sections that show what customers can buy in the restaurant.
 * You can read more about menus in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Menu {
  /**
   * Menu ID.
   * @readonly
   */
  menuId?: string | null;
  /** Menu name. */
  name?: string | null;
  /** Menu description. */
  description?: string | null;
  /** URL of the menu's image file. */
  imageUrl?: string | null;
  /**
   * Visibility criteria that must be met for the menu to appear in the live site.
   * In case of multiple visibility criteria, every criterion must be fulfilled.
   */
  visibilityCriteria?: VisibilityCriteria;
  /** IDs of the sections that are included in the menu. */
  sectionIds?: MenuSectionIds;
  /**
   * Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated.
   * @readonly
   */
  archived?: boolean | null;
}

export interface MenuSectionIds {
  values?: string[];
}

/**
 * Customers can use a discount to pay less. Can be an amount or a percentage.
 * You can read more about discounts in the [introduction](https://dev.wix.com/api/rest/wix-restaurants/catalogs/introduction).
 */
export interface Discount
  extends DiscountValueOneOf,
    DiscountApplyToFilterOneOf {
  /** Discount amount. */
  amount?: Money;
  /**
   * Discount percentage.
   * @decimalValue options { maxScale:3 }
   */
  percentage?: string | null;
  /** IDs of the sections the discount applies to. */
  sectionIds?: SectionIds;
  /** IDs of the items the discount applies to. **Note:** The items must be of type `dish`. */
  itemIds?: ItemIds;
  /**
   * Discount ID.
   * @readonly
   */
  id?: string | null;
  /** Discount name. */
  name?: string | null;
  /** Discount description. */
  description?: string | null;
  /** Whether the discount is active. Defaults to `true`. */
  active?: boolean | null;
  /** Discount type. */
  type?: DiscountTypeWithLiterals;
  /**
   * Discount condition.
   * All conditions must be met so that a customer can apply the discount.
   */
  condition?: DiscountCondition;
}

/** @oneof */
export interface DiscountValueOneOf {
  /** Discount amount. */
  amount?: Money;
  /**
   * Discount percentage.
   * @decimalValue options { maxScale:3 }
   */
  percentage?: string | null;
}

/** @oneof */
export interface DiscountApplyToFilterOneOf {
  /** IDs of the sections the discount applies to. */
  sectionIds?: SectionIds;
  /** IDs of the items the discount applies to. **Note:** The items must be of type `dish`. */
  itemIds?: ItemIds;
}

export enum DiscountType {
  UNSPECIFIED_TYPE = 'UNSPECIFIED_TYPE',
  OFF_ITEM = 'OFF_ITEM',
  OFF_ORDER = 'OFF_ORDER',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNSPECIFIED_TYPE'
  | 'OFF_ITEM'
  | 'OFF_ORDER';

export interface SectionIds {
  values?: string[];
}

export interface ItemIds {
  values?: string[];
}

export interface DiscountCondition {
  /**
   * Which fulfillment types the discount applies to.
   * @minSize 1
   * @maxSize 2
   */
  fulfillmentTypes?: FulfillmentTypeWithLiterals[];
  /**
   * Which ordering platforms the discount applies to.
   * @minSize 1
   * @maxSize 3
   */
  platforms?: DiscountPlatformWithLiterals[];
  /** List of times when the discount is available. */
  availability?: Availability;
  /** Minimum order price for the discount. */
  minOrderPrice?: Money;
  /**
   * Coupon associated with the discount.
   * @readonly
   */
  coupon?: Coupon;
}

export enum DiscountPlatform {
  UNSPECIFIED_PLATFORM = 'UNSPECIFIED_PLATFORM',
  SITE = 'SITE',
  MOBILE_SITE = 'MOBILE_SITE',
  CALL_CENTER = 'CALL_CENTER',
}

/** @enumType */
export type DiscountPlatformWithLiterals =
  | DiscountPlatform
  | 'UNSPECIFIED_PLATFORM'
  | 'SITE'
  | 'MOBILE_SITE'
  | 'CALL_CENTER';

export interface Coupon {
  /**
   * Whether the customer needs to enter the coupon code to receive the discount. Defaults to `true`.
   * @readonly
   */
  applied?: boolean | null;
  /**
   * Coupon code.
   * @readonly
   */
  code?: string | null;
}

export interface PublishCatalogUpdated {
  /** @format GUID */
  metaSiteId?: string;
  /** @format GUID */
  locationId?: string | null;
  /** @format GUID */
  catalogId?: string | null;
  archived?: boolean;
}

export interface IndexCacheCmd {
  /** @format GUID */
  catalogId?: string;
  orgId?: string;
  orgLocale?: string;
  /** @format GUID */
  metaSiteId?: string | null;
}

export interface CatalogChanged {
  /** Changed Catalog. */
  catalog?: Catalog;
}

export interface ListCatalogsRequest {
  /** Sort order. Defaults to `ASC`. */
  sort?: Sorting;
  /** Paging information. `offset` defaults to `0` and `limit` defaults to `50`. The maximum for limit is `1,000`. */
  paging?: Paging;
  /** Whether archived catalogs are returned. Defaults to `false`. */
  archived?: boolean;
  /**
   * IDs of the locations for which the catalog will be returned. See the [Locations API](https://dev.wix.com/api/rest/business-info/locations/introduction) for more details.
   * @format GUID
   */
  locationIds?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListCatalogsResponse {
  /** Retrieved catalogs. */
  catalogs?: Catalog[];
}

export interface GetMenuRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Menu ID. */
  menuId: string;
}

export interface GetMenuResponse {
  /** Retrieved menu. */
  menu?: Menu;
}

export interface GetSectionRequest {
  /**
   * ID of the catalog the section belongs to.
   * @format GUID
   */
  catalogId: string;
  /** ID of the menu the catalog belongs to. */
  menuId: string;
  /** Section ID. */
  sectionId: string;
}

export interface GetSectionResponse {
  /** Retrieved section. */
  section?: Section;
}

export interface UpdateMenuRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Menu to update. */
  menu?: Menu;
}

export interface UpdateMenuResponse {
  /** Updated Menu. */
  menu?: Menu;
}

export interface V3UpdateSectionRequest {
  /**
   * ID of the catalog the section belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Menu ID. */
  menuId: string;
  /** Section to update. */
  section?: Section;
}

export interface UpdateSectionResponse {
  /** Updated section. */
  section?: Section;
}

export interface ListMenusRequest {
  /**
   * ID of the catalog the menus belong to.
   * @format GUID
   */
  catalogId: string;
  /** Field mask path. */
  fieldMask?: string[];
  /** Whether archived menus are returned. Defaults to `false`. */
  archived?: boolean;
}

export interface ListMenusResponse {
  /** Retrieved menus. */
  menus?: Menu[];
}

export interface ListSectionsRequest {
  /**
   * ID of the catalog the sections belong to.
   * @format GUID
   */
  catalogId: string;
  /** Field mask path. */
  fieldMask?: string[];
  /** Whether archived sections are returned. Defaults to `false`. */
  archived?: boolean;
}

export interface ListSectionsResponse {
  /** Sections list. */
  sections?: Section[];
}

export interface CreateMenuRequest {
  /**
   * ID of the catalog the menu will belong to.
   * @format GUID
   */
  catalogId: string;
  /** Menu to create. */
  menu: Menu;
}

export interface CreateMenuResponse {
  /** Created Menu. */
  menu?: Menu;
}

export interface ArchiveMenuRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Menu ID. */
  menuId: string;
}

export interface ArchiveMenuResponse {
  /** Archived menu. */
  menu?: Menu;
}

export interface UnarchiveMenuRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Menu ID. */
  menuId: string;
}

export interface UnarchiveMenuResponse {
  /** Unarchived menu. */
  menu?: Menu;
}

export interface ArchiveSectionRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId?: string;
  /** Menu ID. */
  menuId?: string;
  /** Section ID. */
  sectionId?: string;
}

export interface ArchiveSectionResponse {
  /** Archived section. */
  section?: Section;
}

export interface RemoveDishFromSectionRequest {
  /**
   * ID of the catalog the menu belongs to.
   * @format GUID
   */
  catalogId?: string;
  /** Menu ID. */
  menuId?: string;
  /** Section ID. */
  sectionId?: string;
  /** Item ID. */
  itemId?: string;
}

export interface RemoveDishFromSectionResponse {}

export interface V3CreateSectionRequest {
  /**
   * ID of the catalog the section will belong to.
   * @format GUID
   */
  catalogId: string;
  /** ID of the menu the section will belong to. */
  menuId: string;
  /** Section to create. */
  section: Section;
}

export interface CreateSectionResponse {
  /** Created section. */
  section?: Section;
}

export interface GetItemRequest {
  /**
   * ID of the catalog the item belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Item ID. */
  itemId: string;
  /** Whether `visibilityCriteria` is returned. Defaults to `false`. */
  includeVisibilityCriteria?: boolean;
}

export interface GetItemResponse {
  /** Retrieved item. */
  item?: Item;
}

export interface ListItemsRequest {
  /** Field mask path. */
  fieldMask?: string[];
  /**
   * ID of the catalog the items belong to.
   * @format GUID
   */
  catalogId: string;
  /** Whether archived items are returned. Defaults to `false`. */
  archived?: boolean;
}

export interface ListItemsResponse {
  /** Retrieved items. */
  items?: Item[];
}

export interface UpdateItemRequest {
  /**
   * ID of the catalog the item belongs to.
   * @format GUID
   */
  catalogId: string;
  /** Item to update. */
  item?: Item;
}

export interface UpdateItemResponse {
  /** Updated Item. */
  item?: Item;
}

export interface V3CreateDishRequest {
  /**
   * ID of the catalog the dish will belong to.
   * @format GUID
   */
  catalogId: string;
  /** ID of the menu the dish will belong to. */
  menuId: string;
  /** ID of the section the dish will belong to. */
  sectionId: string;
  /** Item of type `dish` to create. */
  dish: Item;
}

export interface CreateDishResponse {
  /** Created Dish. */
  dish?: Item;
}

export interface CreateVariationRequest {
  /**
   * ID of the catalog the variation will belong to.
   * @format GUID
   */
  catalogId: string;
  /** Variation name. */
  name: string;
}

export interface CreateVariationResponse {
  /** Created Variation. */
  variation?: Item;
}

export interface BulkCreateVariationsRequest {
  /** ID of the catalog the variations will belong to. */
  catalogId: string;
  /**
   * Variations to create. Limited to 20 variations.
   * @minSize 1
   * @maxSize 20
   */
  variations?: NewVariation[];
  /** Whether the full variation entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface NewVariation {
  /**
   * Variation name.
   * @minLength 1
   */
  name?: string;
}

export interface BulkCreateVariationsResponse {
  /** Information about the created variations. */
  results?: BulkItemResult[];
  /** Bulk Create Variations metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkItemResult {
  /** Metadata of the item. */
  entityMetadata?: ItemMetadata;
  /** Item. */
  entity?: Item;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkArchiveMenusRequest {
  /** ID of the catalog the menus belong to. */
  catalogId: string;
  /**
   * IDs of the menus to archive.
   * @minSize 1
   * @maxSize 20
   */
  ids?: string[];
  /** Whether the full menu entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface BulkArchiveMenusResponse {
  /** Information about the archived menus. */
  results?: BulkMenuResult[];
  /** Bulk Archive Menus metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkMenuResult {
  /** Menu metadata. */
  entityMetadata?: ItemMetadata;
  /** Menu. */
  entity?: Menu;
}

export interface BulkCreateDishesRequest {
  /** ID of the catalog the dishes will belong to. */
  catalogId: string;
  /**
   * Information about the dishes to create. Limited to 20 dishes.
   * @minSize 1
   * @maxSize 20
   */
  createDishRequests?: CreateDishRequest[];
  /** Whether the full item entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface CreateDishRequest {
  /** ID of the menu the dish will belong to. */
  menuId?: string;
  /** ID of the section the dish will belong to. */
  sectionId?: string;
  /** Dish to create. */
  dish?: Item;
}

export interface BulkCreateDishesResponse {
  /** Information about the created dishes. */
  results?: BulkItemResult[];
  /** Bulk Create Dishes metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateSectionsRequest {
  /** ID of the catalog the sections will belong to. */
  catalogId: string;
  /**
   * Information about the sections to create. Limited to 20 sections.
   * @minSize 1
   * @maxSize 20
   */
  createSectionRequests?: CreateSectionRequest[];
  /** Whether the full section entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface CreateSectionRequest {
  /** ID of the menu the section will belong to. */
  menuId?: string;
  /** Section to create. */
  section?: Section;
}

export interface BulkCreateSectionsResponse {
  /** Information about the created sections. */
  results?: BulkSectionResult[];
  /** Bulk Create Sections metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkSectionResult {
  /** Section metadata. */
  entityMetadata?: ItemMetadata;
  /** Section. */
  entity?: Section;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateDraftCatalogRequest {
  /**
   * ID of the catalog to create a draft version for.
   * @format GUID
   */
  catalogId: string;
}

export interface CreateDraftCatalogResponse {
  /** Created draft catalog. */
  catalog?: Catalog;
}

export interface PublishDraftCatalogRequest {
  /**
   * ID of the draft catalog to publish.
   * @format GUID
   */
  catalogId: string;
}

export interface PublishDraftCatalogResponse {
  /** Published catalog. */
  catalog?: Catalog;
}

export interface DiscardDraftCatalogRequest {
  /**
   * ID of the draft catalog to discard.
   * @format GUID
   */
  catalogId: string;
}

export interface DiscardDraftCatalogResponse {}

export interface BulkUpdateItemsRequest {
  /** ID of the catalog the items belong to. */
  catalogId: string;
  /**
   * Items to update. Limited to 20 items.
   * @minSize 1
   * @maxSize 20
   */
  items?: Item[];
  /** Whether the full item entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface BulkUpdateItemsResponse {
  /** Information about the updated items. */
  results?: BulkItemResult[];
  /** Bulk Update Items metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateMenusRequest {
  /** ID of the catalog the menus will belong to. */
  catalogId: string;
  /**
   * Information about the menus to create. Limited to 20 menus.
   * @minSize 1
   * @maxSize 20
   */
  menus?: Menu[];
  /** Whether the full menu entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface BulkCreateMenusResponse {
  /** Information about the created menus. */
  results?: BulkMenuResult[];
  /** Bulk Create Menus metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateMenusRequest {
  /** ID of the catalog the menus belong to. */
  catalogId: string;
  /**
   * Menus to update.
   * @minSize 1
   * @maxSize 20
   */
  menus?: Menu[];
  /** Whether the full menu entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface BulkUpdateMenusResponse {
  /** Information about the updated menus. */
  results?: BulkMenuResult[];
  /** Bulk Update Menus metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateSectionsRequest {
  /** ID of the catalog the sections belong to. */
  catalogId: string;
  /**
   * Information about the sections to update. Limited to 20 sections.
   * @minSize 1
   * @maxSize 20
   */
  updateSectionsRequests?: UpdateSectionRequest[];
  /** Whether the full section entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface UpdateSectionRequest {
  /** ID of the menu the section will belong to. */
  menuId?: string;
  /** Section to update. */
  section?: Section;
}

export interface BulkUpdateSectionsResponse {
  /** Information about the updated sections. */
  results?: BulkSectionResult[];
  /** Bulk Update Sections metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUnarchiveMenusRequest {
  /** ID of the catalog the menus belong to. */
  catalogId: string;
  /**
   * IDs of the menus to unarchive.
   * @minSize 1
   * @maxSize 20
   */
  ids?: string[];
  /** Whether the full menu entity is returned. Defaults to `true`. */
  returnFullEntity?: boolean;
}

export interface BulkUnarchiveMenusResponse {
  /** Information about the unarchived menus. */
  results?: BulkMenuResult[];
  /** Bulk Unarchive Menus metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}
